import { h } from './dom.js';

// The creator chat, ported from prototype/viewer/chat.js onto cp-api turns: submit a prompt (with an
// Idempotency-Key), follow the turn's stages over SSE (polling if the stream cannot be opened), then show the
// reply and the new version. Reopening a creation replays its history and resumes a turn still in progress.

const TERMINAL = new Set(['ready', 'clarification', 'failed', 'cancelled']);
const STAGE_TEXT = {
  queued: 'Queued…',
  interpreting: 'Reading your request…',
  validating: 'Checking the edit…',
  repairing: 'Fixing up the edit…',
  generating: 'Creating artwork…',
  rendering: 'Rendering…',
  reviewing: 'Checking the result…',
};
const ERROR_TEXT = {
  turn_in_progress: 'A change is still being made. Wait for it to finish.',
  creation_archived: 'This creation is archived and read-only.',
  creation_blocked: 'This creation is blocked.',
  invalid_prompt: 'That prompt is not valid (1 to 2,000 characters).',
};
const POLL_MS = 2000;

function seconds(turn) {
  if (!turn.started_at || !turn.finished_at) return null;
  return (new Date(turn.finished_at) - new Date(turn.started_at)) / 1000;
}

function describe(turn) {
  const parts = [];
  if (turn.result_version) parts.push(`v${turn.result_version}`);
  const s = seconds(turn);
  if (s !== null) parts.push(`${s.toFixed(1)} s`);
  if (turn.usage?.cache_read) parts.push(`${turn.usage.cache_read} cached tokens`);
  if (Array.isArray(turn.report)) {
    for (const r of turn.report) {
      if (r.warning || r.note) parts.push(`${r.warning ? '⚠' : 'ℹ'} layer ${r.layer} (${r.op}): ${r.warning ?? r.note}`);
    }
  }
  return parts.join(' · ');
}

export function attachChat(workspace) {
  const { api, creation, detail, root } = workspace;
  const log = h('div.log', { 'aria-live': 'polite' });
  const input = h('textarea.prompt', { placeholder: 'Describe the look you want…', maxLength: 2000, rows: 3 });
  const send = h('button.send', { type: 'submit' }, 'Send');
  const form = h('form.prompt-form', input, send);
  const title = creation.title ?? workspace.assetName ?? creation.asset_id;
  root.append(h('aside.chat',
    h('header.chat-header', h('a', { href: location.pathname }, '← All creations'), h('span', title)),
    log, form));

  const add = (role, text, meta) => {
    const el = h(`div.msg.${role.split(' ').join('.')}`, text, meta ? h('div.meta', meta) : null);
    log.append(el);
    log.scrollTop = log.scrollHeight;
    return el;
  };
  const busy = (on) => {
    send.disabled = on;
    input.disabled = on;
    if (!on) input.focus();
  };

  // Render a finished turn's answer (the creator's prompt is already shown).
  async function finish(turn) {
    if (turn.stage === 'ready') {
      add('assistant', turn.reply ?? 'Done.', describe(turn));
      try {
        await workspace.show(turn.result_version);
        workspace.setHud();
      } catch (err) {
        add('error', `Could not load v${turn.result_version}: ${err.message}`);
      }
    } else if (turn.stage === 'clarification') {
      add('assistant question', turn.reply ?? 'Could you say more?');
    } else if (turn.stage === 'cancelled') {
      add('assistant note', 'Cancelled.');
    } else {
      add('error', turn.error?.message ?? `Failed (${turn.error?.code ?? 'unknown error'}).`);
    }
  }

  // Follow a turn until it finishes: SSE for stage updates, then the turn itself for the reply.
  function follow(turnId) {
    return new Promise((resolve) => {
      const status = h('span', 'Working…');
      const cancel = h('button.cancel', {
        type: 'button',
        onclick: async () => {
          cancel.disabled = true;
          try {
            await api.cancelTurn(turnId);
            status.textContent = 'Cancelling…';
          } catch (err) {
            if (err.code !== 'turn_finished') cancel.disabled = false;
          }
        },
      }, 'Cancel');
      const pending = add('assistant pending', status);
      pending.append(cancel);

      let done = false;
      let source;
      let poller;
      const end = async () => {
        if (done) return;
        done = true;
        source?.close();
        clearInterval(poller);
        let turn;
        for (let attempt = 0; attempt < 5 && !turn; attempt++) {
          try {
            turn = (await api.turn(turnId)).turn;
          } catch {
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
        pending.remove();
        if (turn) await finish(turn);
        else add('error', 'Lost track of this change. Reload to see where it got to.');
        resolve();
      };
      const onStage = (stage) => {
        if (TERMINAL.has(stage)) end();
        else if (STAGE_TEXT[stage]) status.textContent = STAGE_TEXT[stage];
      };
      const poll = () => {
        if (poller) return;
        poller = setInterval(async () => {
          try {
            const { turn } = await api.turn(turnId);
            onStage(turn.stage);
          } catch {
            // keep polling; a 401 shows the login screen
          }
        }, POLL_MS);
      };

      source = new EventSource(api.eventsUrl(turnId), { withCredentials: true });
      for (const stage of [...Object.keys(STAGE_TEXT), ...TERMINAL]) {
        source.addEventListener(stage, () => onStage(stage));
      }
      // EventSource reconnects by itself (with Last-Event-ID) when cp-api ends the stream at APP_SSE_MAX_SECS or on
      // shutdown. If it gives up (CLOSED: a non-200, e.g. 401), poll instead.
      source.onerror = () => {
        if (source.readyState === EventSource.CLOSED) poll();
      };
      // The turn may have finished before the stream opened; the stream replays from the start, but check anyway.
      api.turn(turnId).then(({ turn }) => onStage(turn.stage)).catch(() => {});
    });
  }

  async function submit(prompt) {
    add('creator', prompt);
    busy(true);
    const key = crypto.randomUUID();
    try {
      let response;
      for (let attempt = 0; ; attempt++) {
        try {
          response = await api.submitTurn(creation.id, prompt, key);
          break;
        } catch (err) {
          // A network failure may have reached cp-api; the same key returns the original turn, never a second one.
          if (err.status !== 0 || attempt >= 2) throw err;
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
      await follow(response.turn.id);
    } catch (err) {
      const text = err.code === 'rate_limited'
        ? `Too many changes in a row. Try again in ${err.retryAfter ?? 60} s.`
        : ERROR_TEXT[err.code] ?? err.message;
      add('error', text);
    } finally {
      busy(false);
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const prompt = input.value.trim();
    if (!prompt || send.disabled) return;
    input.value = '';
    submit(prompt);
  });
  // Enter sends; Shift+Enter is a new line.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit();
    }
  });

  // History (the newest page, oldest first), then resume a turn still running.
  (async () => {
    let running = null;
    for (const turn of detail.turns) {
      add('creator', turn.prompt);
      if (TERMINAL.has(turn.stage)) await finishHistory(turn);
      else running = turn;
    }
    if (creation.state !== 'draft') {
      add('assistant note', ERROR_TEXT[creation.state === 'archived' ? 'creation_archived' : 'creation_blocked']);
      busy(true);
      send.disabled = true;
      return;
    }
    if (running) {
      busy(true);
      await follow(running.id);
      busy(false);
    } else {
      input.focus();
    }
  })();

  // History entries: the reply only. The viewer already shows the latest version.
  async function finishHistory(turn) {
    if (turn.stage === 'ready') add('assistant', turn.reply ?? 'Done.', describe(turn));
    else if (turn.stage === 'clarification') add('assistant question', turn.reply ?? '');
    else if (turn.stage === 'cancelled') add('assistant note', 'Cancelled.');
    else add('error', turn.error?.message ?? `Failed (${turn.error?.code ?? 'unknown error'}).`);
  }
}
